/**
 * Card history for Standout Review.
 *
 * One card per student per class session inside the last 3 months. That is
 * the same clustering as a parent update: `classSessionId`. A note with no
 * class session stays on its own staff card so it is not dropped. Parents
 * omit that note. The page groups those cards by day, then by class, with
 * this week (Monday through Sunday) first. A finished card leaves that
 * group and sorts to the bottom.
 *
 * The card splits by share state, not by date (`splitReviewCard`). The left
 * column is still to decide. The right column is decided: shared, and,
 * when the card's show-hidden box is on, don't-share and hidden scans.
 * The left column is cleared when every item passes `isShareDecided`. A
 * class scan sits on the comment of the person who took it when that person
 * wrote a note here. The scanner kiosk is not a byline, so those scans, and
 * any photo whose photographer wrote no note, sit in the top divider.
 * Moment photos stay on the note they were taken with. The photo and the
 * message keep separate share state.
 *
 * Story Time only joins a session card that a filtered note or class scan
 * already opened. A recording alone does not put a filtered-out kid back
 * on the page.
 *
 * Only the last 3 months. No paging.
 */
import dayjs from "dayjs"

import { getArtworkDisplayUrl } from "#components/forms/AddStudent/studentArtworkTilesHelpers"
import {
  type ReviewClassImage,
  type ReviewClassTeachers,
} from "#components/forms/AddStudent/studentArtworkTilesHelpers"
import { type StandoutMomentConstructor } from "#customTypes/composedTypes/standoutTypes"
import { type StoryTimeConstructor } from "#customTypes/composedTypes/storyTimeTypes"
import {
  ARTWORK_CAPTURED_BY,
  type StudentArtwork,
} from "#customTypes/composedTypes/studentTypes"
import { isHistoryMessageShared } from "#utils/standoutMomentMigration"
import { isStoryTimeShared } from "#utils/storyTimeHelpers"

import { momentHasFamilyText } from "./standoutSessionGroups"

export { isHistoryMessageShared }

export const historyWindowStart = (now: number): number =>
  dayjs(now).subtract(3, "month").valueOf()

const parseTime = (
  value: string | number | null | undefined
): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const parsed = Date.parse(value)
    if (!Number.isNaN(parsed)) return parsed
  }
  return null
}

const classHasTeacher = (
  classObj: ReviewClassTeachers | undefined,
  teacherId: string
): boolean =>
  (classObj?.teacherIds ?? []).includes(teacherId) ||
  classObj?.defaultTeacherId === teacherId

/**
 * Class scans the history can place. Scans with no class session or image
 * are omitted. Hidden scans stay, so a card can reveal them. The 3-month
 * window is applied when the cards are built, so a scan older than that
 * can still sit on a message from inside the window.
 */
export const selectHistoryScans = (
  students: readonly {
    id: string
    artwork?: Record<string, StudentArtwork> | null
  }[],
  options: {
    classId: string | null
    teacherId: string | null
    classesById: Record<string, ReviewClassTeachers | undefined>
    studentSearch: string
    namesByStudentId: Record<string, string | undefined>
  }
): ReviewClassImage[] => {
  const search = options.studentSearch.trim().toLowerCase()
  const rows: ReviewClassImage[] = []
  for (const student of students) {
    if (search) {
      const name = options.namesByStudentId[student.id] ?? student.id
      if (!name.toLowerCase().includes(search)) continue
    }
    for (const piece of Object.values(student.artwork ?? {})) {
      if (!piece?.id || !piece.classSessionId) continue
      if (!getArtworkDisplayUrl(piece)) continue
      if (options.classId !== null && piece.classId !== options.classId)
        continue
      if (
        options.teacherId !== null &&
        !classHasTeacher(options.classesById[piece.classId], options.teacherId)
      ) {
        continue
      }
      rows.push({ studentId: student.id, artwork: piece })
    }
  }
  return rows
}

/**
 * Every class-session photo on these students. Hidden photos and photos
 * with no display URL stay in, because the family-send clock still has to
 * see them. The history cards use `selectHistoryScans`, which drops a scan
 * with no image or no class session.
 */
export const selectSessionArtwork = (
  students: readonly {
    id: string
    artwork?: Record<string, StudentArtwork> | null
  }[]
): ReviewClassImage[] => {
  const rows: ReviewClassImage[] = []
  for (const student of students) {
    for (const piece of Object.values(student.artwork ?? {})) {
      if (!piece?.id || !piece.classSessionId) continue
      rows.push({ studentId: student.id, artwork: piece })
    }
  }
  return rows
}

/** Letters only. `LA^` is LA, `R` is R. */
export const skillAbbreviation = (level: string | null | undefined): string =>
  (level ?? "").replace(/[^A-Za-z]/g, "")

export type HistoryItem =
  | {
      kind: "message"
      id: string
      at: number
      moment: StandoutMomentConstructor
    }
  | {
      kind: "photo"
      id: string
      at: number
      scan: ReviewClassImage
    }

/**
 * A class scan or a moment photo. `authorId` is the person who took it.
 * Null means the photographer is not a note author we can match (a kiosk
 * scan, for example), so the photo belongs in the top divider.
 */
export type SessionPhoto = {
  id: string
  at: number
  url: string
  caption: string
  authorId: string | null
  shared: boolean
  scan: ReviewClassImage | null
  momentId: string | null
  /** Moment media storage path. Empty for a class scan, which uses `url`. */
  storagePath: string
  /** Class scan marked hidden. The card reveals it with don't-share. */
  hidden?: boolean
}

/** One note in this class session, with the photos that person took. */
export type SessionComment = {
  id: string
  at: number
  moment: StandoutMomentConstructor
  shared: boolean
  photos: SessionPhoto[]
}

/** Story Time for this session. Shared clips sit with the decided items. */
export type SessionAudio = {
  id: string
  at: number
  url: string
  durationSeconds: number | null
  shared: boolean
  momentId: string | null
  createdBy?: string
}

/**
 * A comment from before the week on the card. Not a second card. Shared
 * ones are compact right-column items; the rest are full left-column cards.
 */
export type EarlierComment = SessionComment & {
  classId: string | undefined
  text: string
  /** Same author fields the card byline uses. */
  createdBy?: string | null
  assistantId?: string | null
}

export type HistoryCard = {
  id: string
  /** Monday that starts the week of this card, YYYY-MM-DD. */
  week: string
  at: number
  studentId: string
  classSessionId: string | null
  classId: string | undefined
  /**
   * Newest note, photo, or Story Time on the card. The header prefers the
   * class session's own start when that session is loaded.
   */
  date: number | null
  projectId: string | null
  /** This session's notes, newest first. */
  comments: SessionComment[]
  /** Photos whose photographer did not write a note on this card. */
  topPhotos: SessionPhoto[]
  audio: SessionAudio[]
  /** Notes and class scans, for the status pill and the repeat judgment. */
  items: HistoryItem[]
  /**
   * Notes folded onto this card by an older build of the review. New cards
   * leave this empty: each session is its own card under its day.
   */
  earlierComments: EarlierComment[]
}

/**
 * Monday that starts the local week containing `at`. The review week is
 * Monday through Sunday, whatever the dayjs locale uses for `startOf`.
 */
export const reviewWeekStart = (at: number) => {
  const day = dayjs(at)
  const fromMonday = (day.day() + 6) % 7
  return day.startOf("day").subtract(fromMonday, "day")
}

/** Monday that starts the local week containing `at`, YYYY-MM-DD. */
export const reviewWeekKey = (at: number): string =>
  reviewWeekStart(at).format("YYYY-MM-DD")

const byNewestThenId = (
  a: { at: number; id: string },
  b: { at: number; id: string }
): number => {
  if (a.at !== b.at) return b.at - a.at
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
}

/**
 * Minutes from midnight for a class `startTime` (`h:mm a`). Morning is
 * smaller. A blank or unreadable time is null so those cards sort last.
 */
export const classStartMinutes = (
  startTime: string | null | undefined
): number | null => {
  const text = startTime?.trim().toLowerCase() ?? ""
  const match = text.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/)
  if (!match) return null
  let hour = Number(match[1])
  const minute = Number(match[2] ?? "0")
  const meridiem = match[3]
  if (minute > 59) return null
  if (meridiem) {
    if (hour < 1 || hour > 12) return null
    if (meridiem === "am" && hour === 12) hour = 0
    if (meridiem === "pm" && hour !== 12) hour += 12
  } else if (hour > 23) {
    return null
  }
  return hour * 60 + minute
}

/**
 * Later class start first. A missing start time sorts last. Class name
 * breaks a tie. Same class keeps the newer card first.
 */
export const compareHistoryCardsByClassStart = (
  a: HistoryCard,
  b: HistoryCard,
  classesById: Record<
    string,
    { name?: string | null; startTime?: string | null } | undefined
  >
): number => {
  const classA = a.classId ? classesById[a.classId] : undefined
  const classB = b.classId ? classesById[b.classId] : undefined
  const startA = classStartMinutes(classA?.startTime)
  const startB = classStartMinutes(classB?.startTime)
  if ((startA == null) !== (startB == null)) return startA == null ? 1 : -1
  if (startA != null && startB != null && startA !== startB) {
    return startB - startA
  }
  const byName = (classA?.name ?? "")
    .trim()
    .localeCompare((classB?.name ?? "").trim(), undefined, {
      sensitivity: "base",
    })
  if (byName !== 0) return byName
  return byNewestThenId(a, b)
}

type ClassStartLookup = Record<
  string,
  { name?: string | null; startTime?: string | null } | undefined
>

type SessionBucket = {
  studentId: string
  classSessionId: string | null
  classId: string | undefined
  messages: StandoutMomentConstructor[]
  scans: ReviewClassImage[]
  stories: StoryTimeConstructor[]
}

const sessionCardId = (
  studentId: string,
  classSessionId: string | null
): string => `${studentId}::${classSessionId ?? "none"}`

const momentAuthorId = (
  moment: Pick<StandoutMomentConstructor, "assistantId" | "createdBy">
): string | null => {
  const assistant = moment.assistantId?.trim()
  if (assistant) return assistant
  const created = moment.createdBy?.trim()
  return created || null
}

/** Teacher-app scans name a person. The kiosk does not. */
const scanAuthorId = (artwork: StudentArtwork): string | null => {
  if (artwork.capturedBy !== ARTWORK_CAPTURED_BY.TEACHER_APP) return null
  const id = artwork.createdBy?.trim()
  return id || null
}

const scanPhoto = (scan: ReviewClassImage): SessionPhoto => ({
  id: scan.artwork.id,
  at: parseTime(scan.artwork.timeCreated) ?? 0,
  url: getArtworkDisplayUrl(scan.artwork),
  caption: scan.artwork.title?.trim() ?? "",
  authorId: scanAuthorId(scan.artwork),
  shared: scan.artwork.isShared === true,
  scan,
  momentId: null,
  storagePath: "",
  hidden: scan.artwork.isHidden === true,
})

const durationSeconds = (
  durationMs: number | null | undefined
): number | null => {
  if (typeof durationMs !== "number" || !Number.isFinite(durationMs))
    return null
  if (durationMs <= 0) return null
  return Math.round(durationMs / 1000)
}

/** A note with the photos taken with it. Those photos share with the note. */
const sessionComment = (
  moment: StandoutMomentConstructor,
  at: number
): SessionComment => {
  const shared = isHistoryMessageShared(moment)
  return {
    id: moment.id,
    at,
    moment,
    shared,
    photos: (moment.media ?? []).flatMap((entry) =>
      entry.kind === "photo" && entry.id
        ? [
            {
              id: entry.id,
              at,
              url: "",
              caption: entry.caption?.trim() ?? "",
              authorId: momentAuthorId(moment),
              shared,
              scan: null,
              momentId: moment.id,
              storagePath: entry.storagePath,
              hidden: false,
            },
          ]
        : []
    ),
  }
}

const placeSession = (
  bucket: SessionBucket
): {
  comments: SessionComment[]
  topPhotos: SessionPhoto[]
  audio: SessionAudio[]
  items: HistoryItem[]
  at: number
  projectId: string | null
} => {
  const messagesNewest = [...bucket.messages].sort((a, b) =>
    byNewestThenId(
      { at: parseTime(a.createdAt) ?? 0, id: a.id },
      { at: parseTime(b.createdAt) ?? 0, id: b.id }
    )
  )
  const comments = messagesNewest.map((moment) =>
    sessionComment(moment, parseTime(moment.createdAt) ?? 0)
  )
  const newestByAuthor = new Map<string, SessionComment>()
  for (const comment of comments) {
    const authorId = momentAuthorId(comment.moment)
    if (authorId && !newestByAuthor.has(authorId)) {
      newestByAuthor.set(authorId, comment)
    }
  }
  const topPhotos: SessionPhoto[] = []
  const scansNewest = [...bucket.scans].sort((a, b) =>
    byNewestThenId(
      { at: parseTime(a.artwork.timeCreated) ?? 0, id: a.artwork.id },
      { at: parseTime(b.artwork.timeCreated) ?? 0, id: b.artwork.id }
    )
  )
  for (const scan of scansNewest) {
    const photo = scanPhoto(scan)
    const host = photo.authorId ? newestByAuthor.get(photo.authorId) : undefined
    if (host) host.photos.push(photo)
    else topPhotos.push(photo)
  }
  const audio: SessionAudio[] = [...bucket.stories]
    .filter((story) => story.url.trim().length > 0)
    .sort((a, b) =>
      byNewestThenId(
        { at: parseTime(a.timeCreated) ?? 0, id: a.id },
        { at: parseTime(b.timeCreated) ?? 0, id: b.id }
      )
    )
    .map((story) => ({
      id: story.id,
      at: parseTime(story.timeCreated) ?? 0,
      url: story.url,
      durationSeconds: durationSeconds(story.durationMs),
      shared: isStoryTimeShared(story),
      momentId: null,
      createdBy: story.createdBy,
    }))
  const items: HistoryItem[] = []
  for (const comment of comments) {
    items.push({
      kind: "message",
      id: comment.id,
      at: comment.at,
      moment: comment.moment,
    })
    for (const photo of comment.photos) {
      if (!photo.scan) continue
      items.push({
        kind: "photo",
        id: photo.id,
        at: photo.at,
        scan: photo.scan,
      })
    }
  }
  for (const photo of topPhotos) {
    if (!photo.scan) continue
    items.push({ kind: "photo", id: photo.id, at: photo.at, scan: photo.scan })
  }
  const times = [
    ...comments.map((comment) => comment.at),
    ...topPhotos.map((photo) => photo.at),
    ...comments.flatMap((comment) => comment.photos.map((photo) => photo.at)),
    ...audio.map((clip) => clip.at),
  ]
  const projectId =
    comments.find((comment) => comment.moment.projectId)?.moment.projectId ??
    topPhotos.find((photo) => photo.scan?.artwork.projectId)?.scan?.artwork
      .projectId ??
    null
  return {
    comments,
    topPhotos,
    audio,
    items,
    at: times.length > 0 ? Math.max(...times) : 0,
    projectId,
  }
}

export const buildStandoutReviewHistory = ({
  moments,
  scans,
  storyTimes = [],
  now,
  classesById = {},
}: {
  /** Filtered notes. Each one opens or joins its own class-session card. */
  moments: readonly StandoutMomentConstructor[]
  scans: readonly ReviewClassImage[]
  storyTimes?: readonly StoryTimeConstructor[]
  now: number
  classesById?: ClassStartLookup
}): { cards: HistoryCard[] } => {
  const windowStart = historyWindowStart(now)
  const inWindow = (created: number): boolean => created >= windowStart

  const familyMessages = (rows: readonly StandoutMomentConstructor[]) =>
    rows.filter((moment) => {
      if (!momentHasFamilyText(moment)) return false
      const created = parseTime(moment.createdAt)
      return created != null && inWindow(created)
    })

  const buckets = new Map<string, SessionBucket>()
  const ensure = (
    studentId: string,
    classSessionId: string | null,
    classId: string | undefined
  ): SessionBucket => {
    const id = sessionCardId(studentId, classSessionId)
    const existing = buckets.get(id)
    if (existing) {
      if (!existing.classId && classId) existing.classId = classId
      return existing
    }
    const bucket: SessionBucket = {
      studentId,
      classSessionId,
      classId,
      messages: [],
      scans: [],
      stories: [],
    }
    buckets.set(id, bucket)
    return bucket
  }

  for (const moment of familyMessages(moments)) {
    ensure(
      moment.studentId,
      moment.classSessionId ?? null,
      moment.classId
    ).messages.push(moment)
  }

  for (const scan of scans) {
    const sessionId = scan.artwork.classSessionId
    if (!sessionId) continue
    if (!getArtworkDisplayUrl(scan.artwork)) continue
    const host = [...buckets.values()].find(
      (bucket) =>
        bucket.studentId === scan.studentId &&
        bucket.classSessionId === sessionId &&
        bucket.messages.length > 0
    )
    if (!host) {
      const created = parseTime(scan.artwork.timeCreated)
      if (created == null || !inWindow(created)) continue
      const createdHost = ensure(
        scan.studentId,
        sessionId,
        scan.artwork.classId
      )
      if (!createdHost.classId) createdHost.classId = scan.artwork.classId
      createdHost.scans.push(scan)
      continue
    }
    if (!host.classId) host.classId = scan.artwork.classId
    host.scans.push(scan)
  }

  for (const story of storyTimes) {
    const sessionId = story.classSessionId
    if (!sessionId || !story.url?.trim()) continue
    const created = parseTime(story.timeCreated)
    if (created == null || !inWindow(created)) continue
    // A recording does not open a card. Moments and scans already applied
    // the class, teacher, and student filters. Attaching here keeps a
    // filtered-out kid off the page.
    const host = [...buckets.values()].find(
      (bucket) =>
        bucket.studentId === story.studentId &&
        bucket.classSessionId === sessionId
    )
    if (!host) continue
    host.stories.push(story)
  }

  const cards: HistoryCard[] = []
  for (const bucket of buckets.values()) {
    const placed = placeSession(bucket)
    if (
      placed.comments.length === 0 &&
      placed.topPhotos.length === 0 &&
      placed.audio.length === 0
    ) {
      continue
    }
    cards.push({
      id: sessionCardId(bucket.studentId, bucket.classSessionId),
      week: reviewWeekKey(placed.at || now),
      at: placed.at,
      studentId: bucket.studentId,
      classSessionId: bucket.classSessionId,
      classId: bucket.classId,
      date: placed.at || null,
      projectId: placed.projectId,
      comments: placed.comments,
      topPhotos: placed.topPhotos,
      audio: placed.audio,
      items: placed.items,
      earlierComments: [],
    })
  }

  cards.sort((a, b) => compareHistoryCardsByClassStart(a, b, classesById))
  return { cards }
}

/** Shared and don't-share go right. Hidden scans go right behind the checkbox. Open stays left. A secret stays on the left as a staff note. */
export type ReviewShareState =
  | "shared"
  | "blocked"
  | "hidden"
  | "open"
  | "staff"

/**
 * The page's goal is an empty left column. An item no longer needs anyone
 * once it is shared, marked don't share, or hidden. A secret is not that
 * decision: it stays visible and keeps the card on the review.
 */
export const isShareDecided = (state: ReviewShareState): boolean =>
  state !== "open" && state !== "staff"

export const momentShareState = (
  moment: StandoutMomentConstructor
): ReviewShareState => {
  if (moment.category === "secret") return "staff"
  if (moment.dontShare === true) return "blocked"
  return isHistoryMessageShared(moment) ? "shared" : "open"
}

/** A class scan keeps its own state. A moment photo follows its note. */
const photoShareState = (
  photo: SessionPhoto,
  host: ReviewShareState | null
): ReviewShareState => {
  if (photo.scan) {
    if (photo.hidden === true || photo.scan.artwork.isHidden === true) {
      return "hidden"
    }
    if (photo.scan.artwork.dontShare === true) return "blocked"
    return photo.scan.artwork.isShared === true ? "shared" : "open"
  }
  if (host) return host
  return photo.shared ? "shared" : "open"
}

/** One share state's notes, with the photos that are in the same state. */
export type ReviewShareGroup = {
  /** Loose photos, and photos whose state differs from their note. */
  photos: SessionPhoto[]
  /** Story Time clips in this share state. */
  audio: SessionAudio[]
  /** This session's notes. Each keeps only the photos in this state. */
  comments: SessionComment[]
  /** Notes from before this week, newest first. */
  earlier: EarlierComment[]
  /**
   * `comments` and `earlier` as one list, newest first. The columns show
   * this: when a note is from does not decide where it sits.
   */
  notes: (SessionComment | EarlierComment)[]
}

export type ReviewCardColumns = {
  /** Left: still to decide. */
  open: ReviewShareGroup
  /** Left, always visible: secret notes. Not a family share. */
  staff: ReviewShareGroup
  /** Right, behind show-hidden: don't share. */
  blocked: ReviewShareGroup
  /** Right, behind show-hidden: scans marked hidden. */
  hidden: ReviewShareGroup
  /** Right: compact shared items. */
  shared: ReviewShareGroup
  /** Every item passes `isShareDecided`. */
  cleared: boolean
}

const emptyGroup = (): ReviewShareGroup => ({
  photos: [],
  audio: [],
  comments: [],
  earlier: [],
  notes: [],
})

/** Sorts one card's items into the left and right columns by share state. */
export const splitReviewCard = (card: HistoryCard): ReviewCardColumns => {
  const groups: Record<ReviewShareState, ReviewShareGroup> = {
    open: emptyGroup(),
    staff: emptyGroup(),
    blocked: emptyGroup(),
    hidden: emptyGroup(),
    shared: emptyGroup(),
  }
  const states: ReviewShareState[] = []
  for (const clip of card.audio) {
    const state: ReviewShareState = clip.shared ? "shared" : "open"
    states.push(state)
    groups[state].audio.push(clip)
  }
  for (const photo of card.topPhotos) {
    const state = photoShareState(photo, null)
    states.push(state)
    groups[state].photos.push(photo)
  }
  for (const comment of card.comments) {
    const state = momentShareState(comment.moment)
    states.push(state)
    const beside: SessionPhoto[] = []
    for (const photo of comment.photos) {
      const photoState = photoShareState(photo, state)
      if (photo.scan) states.push(photoState)
      if (photoState === state) beside.push(photo)
      else groups[photoState].photos.push(photo)
    }
    groups[state].comments.push({ ...comment, photos: beside })
  }
  for (const comment of card.earlierComments) {
    const state = momentShareState(comment.moment)
    states.push(state)
    groups[state].earlier.push(comment)
  }
  for (const group of Object.values(groups)) {
    group.notes = [...group.comments, ...group.earlier].sort(byNewestThenId)
  }
  return { ...groups, cleared: states.every(isShareDecided) }
}

/** The whole filtered review has nothing left to decide. */
export const isReviewCleared = (cards: readonly HistoryCard[]): boolean =>
  cards.length > 0 && cards.every((card) => splitReviewCard(card).cleared)

export type ReviewClassGroup = {
  key: string
  classId: string | null
  label: string
  cards: HistoryCard[]
}

export type ReviewDayGroup = {
  key: string
  label: string
  classes: ReviewClassGroup[]
}

const cardAt = (card: HistoryCard): number => card.date ?? card.at

const groupByDayThenClass = (
  rows: readonly HistoryCard[],
  classesById: ClassStartLookup
): ReviewDayGroup[] => {
  const byDay = new Map<string, HistoryCard[]>()
  for (const card of rows) {
    const key = dayjs(cardAt(card)).format("YYYY-MM-DD")
    const list = byDay.get(key) ?? []
    list.push(card)
    byDay.set(key, list)
  }
  return [...byDay.keys()]
    .sort((a, b) => (a < b ? 1 : a > b ? -1 : 0))
    .map((key) => {
      const dayCards = [...(byDay.get(key) ?? [])].sort((a, b) =>
        compareHistoryCardsByClassStart(a, b, classesById)
      )
      const classes: ReviewClassGroup[] = []
      for (const card of dayCards) {
        const classKey = card.classId ?? ""
        let group = classes.find((item) => item.key === classKey)
        if (!group) {
          const classObj = card.classId ? classesById[card.classId] : undefined
          const name = classObj?.name?.trim() || "No class"
          const start = classObj?.startTime?.trim()
          group = {
            key: classKey,
            classId: card.classId ?? null,
            label: start ? `${name} · ${start}` : name,
            cards: [],
          }
          classes.push(group)
        }
        group.cards.push(card)
      }
      return {
        key,
        label: dayjs(key).format("dddd, MMM D"),
        classes,
      }
    })
}

/**
 * Days newest first, this week (Monday–Sunday) before older days. Classes
 * inside a day are later start time first.
 */
export const groupCardsByDayAndClass = (
  cards: readonly HistoryCard[],
  classesById: ClassStartLookup,
  now: number
): { thisWeek: ReviewDayGroup[]; earlier: ReviewDayGroup[] } => {
  const weekStart = reviewWeekStart(now).valueOf()
  const thisWeek: HistoryCard[] = []
  const earlier: HistoryCard[] = []
  for (const card of cards) {
    if (cardAt(card) >= weekStart) thisWeek.push(card)
    else earlier.push(card)
  }
  return {
    thisWeek: groupByDayThenClass(thisWeek, classesById),
    earlier: groupByDayThenClass(earlier, classesById),
  }
}

/**
 * Active cards stay under their day and class, this week first. A card
 * with nothing left on the left sorts into `finished`.
 */
export const groupReviewCards = (
  cards: readonly HistoryCard[],
  classesById: ClassStartLookup,
  now: number
): {
  thisWeek: ReviewDayGroup[]
  earlier: ReviewDayGroup[]
  finished: HistoryCard[]
} => {
  const weekStart = reviewWeekStart(now).valueOf()
  const finished: HistoryCard[] = []
  const thisWeek: HistoryCard[] = []
  const earlier: HistoryCard[] = []
  for (const card of cards) {
    if (splitReviewCard(card).cleared) {
      finished.push(card)
      continue
    }
    if (cardAt(card) >= weekStart) thisWeek.push(card)
    else earlier.push(card)
  }
  finished.sort((a, b) =>
    byNewestThenId({ at: cardAt(a), id: a.id }, { at: cardAt(b), id: b.id })
  )
  return {
    thisWeek: groupByDayThenClass(thisWeek, classesById),
    earlier: groupByDayThenClass(earlier, classesById),
    finished,
  }
}
